// Shift schedule (2026-08-17): the nurse manager defines the facility's shift
// clock — how many shifts, their names and their boundaries.
//
// A published version is a COMPLETE 24-hour schedule effective from a date.
// Two rules shape this whole screen:
//   * shifts must tile the day exactly (no gaps, no overlaps), and
//   * a version may never start today or earlier.
// The second is what stops a manager stripping coverage and discharge rights
// from nurses part-way through a shift they are working. Both are enforced
// server-side too; the client only makes the invalid state unreachable.
//
// A shift's `code` is immutable once published — it is what roster rows and
// bed pins store. Renaming a shift edits its label, never its code, so
// existing rows can never orphan.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography, LinearProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import LockIcon from "@mui/icons-material/Lock";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { validateTiling } from "../shiftWindow";
import {
  getShiftSchedules, publishShiftSchedule, cancelShiftSchedule, listOf,
} from "../api/nurseApi";

// Same palette and ordering rule WeekGrid uses, so a shift is the same colour
// in both places.
const PALETTE = [
  { bg: "#FEF3C7", ink: "#92400E" },
  { bg: "#FFEDD5", ink: "#9A3412" },
  { bg: "#E0E7FF", ink: "#3730A3" },
  { bg: "#DCFCE7", ink: "#166534" },
  { bg: "#FCE7F3", ink: "#9D174D" },
  { bg: "#E0F2FE", ink: "#075985" },
];

const mins = (hhmm) => {
  const [h, m] = String(hhmm || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};
const durationMins = (s) => (((mins(s.end_time) - mins(s.start_time)) % 1440) + 1440) % 1440 || 1440;
const slug = (text) =>
  String(text || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

// A proportional 24-hour bar. Gaps and overlaps are what this exists to show —
// a written error is easy to skim past, a hole in the bar is not.
function DayBar({ shifts }) {
  const ordered = [...shifts].sort((a, b) => mins(a.start_time) - mins(b.start_time));
  return (
    <Box sx={{ display: "flex", height: 28, borderRadius: 1, overflow: "hidden", border: `1px solid ${NURSE_COLORS.border}`, bgcolor: "#F1F5F9" }}>
      {ordered.map((s, i) => {
        const pct = (durationMins(s) / 1440) * 100;
        const c = PALETTE[i % PALETTE.length];
        return (
          <Tooltip key={s.key} title={`${s.label || s.code}: ${s.start_time}–${s.end_time} (${(durationMins(s) / 60).toFixed(1)}h)`}>
            <Box sx={{
              width: `${pct}%`, bgcolor: c.bg, color: c.ink, fontSize: "0.7rem", fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
              borderRight: `1px solid ${NURSE_COLORS.border}`,
            }}>
              {pct > 8 ? (s.label || s.code) : ""}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

// The rollout script seeds version 1 with a sentinel date far in the past so
// every pre-existing roster row resolves under it. That date and the script's
// own note are implementation detail — a manager should see "this is the
// default", not "2000-01-01 — Seeded default (was hardcoded)".
const SEED_DATE = "2000-01-01";
const isSeeded = (v) => v && String(v.effective_from) === SEED_DATE;

let rowKey = 0;
const toRow = (s) => ({ key: `r${rowKey++}`, code: s.code, label: s.label, start_time: s.start_time, end_time: s.end_time, existing: true });

export default function ShiftScheduleTab({ onChanged }) {
  const [versions, setVersions] = useState([]);
  const [today, setToday] = useState("");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  const reload = useCallback(() => {
    setLoading(true);
    getShiftSchedules()
      .then((r) => {
        const list = listOf(r);
        setVersions(list);
        // `today` is the BACKEND's IST date — trust it over the browser when
        // deciding what counts as future, since that is what the API checks.
        if (r?.today) setToday(r.today);
      })
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const inForce = useMemo(
    () => versions.filter((v) => !today || v.effective_from <= today)
      .sort((a, b) => String(b.effective_from).localeCompare(String(a.effective_from)))[0] || null,
    [versions, today]
  );
  const upcoming = useMemo(
    () => versions.filter((v) => today && v.effective_from > today)
      .sort((a, b) => String(a.effective_from).localeCompare(String(b.effective_from))),
    [versions, today]
  );

  // Seed the editor from whatever is in force, so the common edit is a tweak
  // rather than starting from nothing.
  useEffect(() => {
    if (inForce && rows.length === 0) setRows((inForce.shifts || []).map(toRow));
  }, [inForce, rows.length]);

  const tomorrow = useMemo(() => {
    if (!today) return "";
    const d = new Date(`${today}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  }, [today]);
  useEffect(() => { if (tomorrow && !effectiveFrom) setEffectiveFrom(tomorrow); }, [tomorrow, effectiveFrom]);

  const tilingError = useMemo(() => validateTiling(rows), [rows]);

  // Editing a start time drags the PREVIOUS shift's end with it. Without this
  // the very first keystroke of the commonest edit ("start mornings an hour
  // later") invalidates the schedule and the manager has to fix two fields to
  // undo one change.
  const setStart = (idx, value) => {
    setRows((cur) => {
      const next = cur.map((r) => ({ ...r }));
      next[idx].start_time = value;
      const prev = (idx - 1 + next.length) % next.length;
      if (next.length > 1) next[prev].end_time = value;
      return next;
    });
  };
  const setEnd = (idx, value) => {
    setRows((cur) => {
      const next = cur.map((r) => ({ ...r }));
      next[idx].end_time = value;
      const after = (idx + 1) % next.length;
      if (next.length > 1) next[after].start_time = value;
      return next;
    });
  };

  const addRow = () => setRows((cur) => {
    const last = cur[cur.length - 1];
    return [...cur, {
      key: `r${rowKey++}`, code: "", label: "",
      start_time: last ? last.end_time : "00:00",
      end_time: last ? last.end_time : "00:00",
      existing: false,
    }];
  });
  const removeRow = (idx) => setRows((cur) => cur.filter((_, i) => i !== idx));

  const publish = async () => {
    if (tilingError) return;
    const payload = {
      effective_from: effectiveFrom,
      note: note || undefined,
      shifts: rows.map((r, i) => ({
        code: r.code || slug(r.label),
        label: r.label || r.code,
        start_time: r.start_time,
        end_time: r.end_time,
        sort_order: i + 1,
      })),
    };
    setBusy(true);
    setConflicts([]);
    try {
      const res = await publishShiftSchedule(payload);
      if (res?.status !== "Success") {
        // A rejection may carry the exact rows that block it — those are far
        // more actionable than the message alone.
        setConflicts(res?.conflicts || []);
        toast.error(res?.message || "Could not publish the schedule");
        return;
      }
      toast.success(res?.message || "Schedule published");
      reload();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not publish the schedule");
    } finally { setBusy(false); }
  };

  const cancel = async (version) => {
    if (!window.confirm(`Cancel the schedule change effective ${version.effective_from}?`)) return;
    setBusy(true);
    try {
      const res = await cancelShiftSchedule(version.id);
      if (res?.status !== "Success") { toast.error(res?.message || "Could not cancel"); return; }
      toast.success("Scheduled change cancelled");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not cancel");
    } finally { setBusy(false); }
  };

  const publishDisabled = busy || Boolean(tilingError) || !effectiveFrom || (today && effectiveFrom <= today);

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>In force now</Typography>
        {inForce ? (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {isSeeded(inForce)
                ? "This facility's standard shift pattern."
                : `In effect since ${inForce.effective_from}${inForce.note ? ` — ${inForce.note}` : ""}`}
            </Typography>
            <DayBar shifts={(inForce.shifts || []).map((s, i) => ({ ...s, key: `f${i}` }))} />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
              {(inForce.shifts || []).map((s) => (
                <Chip key={s.code} size="small" variant="outlined" label={`${s.label} ${s.start_time}–${s.end_time}`} />
              ))}
            </Stack>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No schedule configured — the built-in default (Morning 06–14, Evening 14–22, Night 22–06) applies.
          </Typography>
        )}
      </Paper>

      {upcoming.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.accent}` }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Scheduled change</Typography>
          {upcoming.map((v) => (
            <Box key={v.id} sx={{ mb: 1.5 }}>
              <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2">
                  Takes effect <b>{v.effective_from}</b>{v.note ? ` — ${v.note}` : ""}
                </Typography>
                <Button size="small" color="error" disabled={busy} onClick={() => cancel(v)}>Cancel</Button>
              </Stack>
              <DayBar shifts={(v.shifts || []).map((s, i) => ({ ...s, key: `u${v.id}-${i}` }))} />
            </Box>
          ))}
        </Paper>
      )}

      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Typography variant="subtitle1">Publish a new schedule</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Shifts must cover all 24 hours, with no gaps or overlaps. Changes start tomorrow at the
          earliest, so a shift already under way is never disturbed.
        </Typography>

        <Box sx={{ mb: 1.5 }}><DayBar shifts={rows} /></Box>

        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>Name</TableCell>
              <TableCell sx={{ width: 170 }}>Code</TableCell>
              <TableCell sx={{ width: 130 }}>Starts</TableCell>
              <TableCell sx={{ width: 130 }}>Ends</TableCell>
              <TableCell sx={{ width: 80 }}>Length</TableCell>
              <TableCell sx={{ width: 56 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.key}>
                <TableCell>
                  <TextField size="small" fullWidth value={r.label}
                    onChange={(e) => setRows((cur) => cur.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                    placeholder="Morning" />
                </TableCell>
                <TableCell>
                  {r.existing ? (
                    <Tooltip title="A published shift's code can never change — roster rows and bed assignments store it. Edit the name instead.">
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                        <LockIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                        <Typography variant="body2" color="text.secondary">{r.code}</Typography>
                      </Stack>
                    </Tooltip>
                  ) : (
                    <TextField size="small" fullWidth value={r.code}
                      onChange={(e) => setRows((cur) => cur.map((x, j) => (j === i ? { ...x, code: slug(e.target.value) } : x)))}
                      placeholder={slug(r.label) || "code"} />
                  )}
                </TableCell>
                <TableCell>
                  <TextField size="small" type="time" value={r.start_time}
                    onChange={(e) => setStart(i, e.target.value)} InputLabelProps={{ shrink: true }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" type="time" value={r.end_time}
                    onChange={(e) => setEnd(i, e.target.value)} InputLabelProps={{ shrink: true }} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {(durationMins(r) / 60).toFixed(1)}h
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="error" disabled={rows.length <= 1} onClick={() => removeRow(i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Add shift</Button>
          <TextField size="small" type="date" label="Takes effect" value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ min: tomorrow || undefined }}
            sx={{ minWidth: 170 }} />
          <TextField size="small" label="Note (optional)" value={note}
            onChange={(e) => setNote(e.target.value)} sx={{ minWidth: 240, flex: 1 }} />
          <Button variant="contained" disabled={publishDisabled} onClick={publish}>
            {busy ? "Publishing..." : "Publish"}
          </Button>
        </Stack>

        {tilingError && <Alert severity="warning" sx={{ mt: 1.5 }}>{tilingError}</Alert>}
        {today && effectiveFrom && effectiveFrom <= today && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            A schedule can only take effect from {tomorrow} onward — changing the clock mid-shift
            would revoke coverage from nurses currently on duty.
          </Alert>
        )}

        {conflicts.length > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {conflicts.length} existing row(s) use a shift this schedule removes — re-roster them first:
            </Typography>
            {conflicts.slice(0, 20).map((c) => (
              <Typography key={`${c.kind}-${c.id}`} variant="caption" component="div">
                {c.kind === "roster" ? "Roster" : "Assignment"} · {c.nurse_name || c.nurse_id} · {c.date}
                {c.bed_code ? ` · ${c.bed_code}` : ""} · <b>{c.shift}</b>
              </Typography>
            ))}
            {conflicts.length > 20 && (
              <Typography variant="caption" component="div">…and {conflicts.length - 20} more.</Typography>
            )}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
